import React, { useEffect, useId, useRef, useState } from 'react';
import { getExpenseTheme } from './expenseGroupTheme';
import { FileText, ImageIcon, Receipt, Upload, X } from 'lucide-react';

const ACCEPT = 'image/*,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024;

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPdf(fileOrMeta) {
  const type = fileOrMeta?.type || '';
  const name = fileOrMeta?.name || '';
  return type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
}

function isImage(fileOrMeta) {
  const type = fileOrMeta?.type || '';
  return type.startsWith('image/');
}

/** Themed receipt upload — hidden native input, drag-and-drop, preview. */
export default function ReceiptFileUpload({
  variant = 'fixed',
  onChange,
  file = null,
  existingReceipt = null,
  label = 'Receipt',
}) {
  const theme = getExpenseTheme(variant);
  const inputId = useId();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');

  const selectedFile = file instanceof File ? file : null;
  const hasExisting = Boolean(existingReceipt?.name || existingReceipt?.url);
  const hasSelection = Boolean(selectedFile || (hasExisting && !selectedFile));

  useEffect(() => {
    if (!selectedFile || !isImage(selectedFile)) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const emitChange = (nextFile, clearExisting = false) => {
    setError('');
    onChange({
      target: {
        name: 'receipt',
        value: '',
        files: nextFile ? { 0: nextFile, length: 1 } : { length: 0 },
        clearReceipt: clearExisting,
      },
    });
  };

  const validateAndSet = (nextFile) => {
    if (!nextFile) return;
    if (nextFile.size > MAX_BYTES) {
      setError(`File must be under ${formatSize(MAX_BYTES)}.`);
      return;
    }
    const okType =
      nextFile.type.startsWith('image/') ||
      nextFile.type === 'application/pdf' ||
      /\.(jpe?g|png|gif|webp|pdf)$/i.test(nextFile.name);
    if (!okType) {
      setError('Please choose a PDF or image file.');
      return;
    }
    emitChange(nextFile);
  };

  const handleInputChange = (e) => {
    const picked = e.target.files?.[0];
    if (picked) validateAndSet(picked);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSet(dropped);
  };

  const handleClear = () => {
    setError('');
    emitChange(null, hasExisting);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayName = selectedFile?.name || existingReceipt?.name || 'Receipt';
  const displaySize = selectedFile?.size ?? existingReceipt?.size;
  const showPdf = selectedFile ? isPdf(selectedFile) : isPdf(existingReceipt);
  const showImagePreview =
    previewUrl || (hasExisting && !selectedFile && existingReceipt?.url && !isPdf(existingReceipt));

  return (
    <div>
      <label htmlFor={inputId} className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </label>

      <input
        ref={inputRef}
        id={inputId}
        name="receipt"
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={handleInputChange}
      />

      {!hasSelection ? (
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-all cursor-pointer ${
            dragOver ? theme.fileZoneActive : theme.fileZone
          }`}
        >
          <div className={`flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200/80 ${theme.fileIcon}`}>
            <Upload className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Upload receipt</p>
            <p className="text-xs text-gray-500 mt-0.5">Drag & drop or click to browse</p>
            <p className="text-[11px] text-gray-400 mt-1">PDF or image · up to {formatSize(MAX_BYTES)}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold ${theme.fileBtn}`}>
            <Receipt className="h-3.5 w-3.5" aria-hidden />
            Choose file
          </span>
        </div>
      ) : (
        <div className={`rounded-xl border ring-1 ring-inset overflow-hidden ${theme.fileChip}`}>
          <div className="flex flex-col sm:flex-row gap-3 p-3">
            <div className="flex shrink-0 items-center justify-center w-full sm:w-20 h-20 rounded-lg bg-white/80 border border-white/60 overflow-hidden">
              {showImagePreview && (previewUrl || existingReceipt?.url) ? (
                <img
                  src={previewUrl || existingReceipt.url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : showPdf ? (
                <FileText className={`h-9 w-9 ${theme.fileIcon}`} aria-hidden />
              ) : (
                <ImageIcon className={`h-9 w-9 ${theme.fileIcon}`} aria-hidden />
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <p className="text-sm font-semibold text-gray-900 truncate" title={displayName}>
                {displayName}
              </p>
              {displaySize != null && (
                <p className="text-xs text-gray-600 mt-0.5">{formatSize(displaySize)}</p>
              )}
              {selectedFile ? (
                <p className={`text-[11px] font-medium mt-1 ${theme.amount}`}>Ready to upload on save</p>
              ) : hasExisting ? (
                <p className="text-[11px] text-gray-500 mt-1">Current attachment</p>
              ) : null}
            </div>
            <div className="flex sm:flex-col gap-2 sm:items-end justify-end shrink-0">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold ${theme.fileBtn}`}
              >
                <Upload className="h-3.5 w-3.5" aria-hidden />
                Replace
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
